import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { AlertTriangle } from 'lucide-react'

export interface TimeRangeInputProps {
  start: string
  end: string
  onChange: (next: { start: string; end: string }) => void
  disabled?: boolean
  startId?: string
  endId?: string
}

/**
 * Story 3.16.1 — par de time pickers (HH:mm) com validação inline.
 *
 * Composto sobre `<Input type="time">` (REUSE — atom existente) + `<Label>`
 * (REUSE) + `<Button>` (REUSE). Validação: se `start >= end`, mostra
 * warning card com botão de swap programático.
 *
 * Trim defensivo no `onChange` evita HH:mm:ss vindo de browsers que
 * incluem segundos (raro mas defensivo).
 */
export function TimeRangeInput({
  start,
  end,
  onChange,
  disabled = false,
  startId = 'time-range-start',
  endId = 'time-range-end',
}: TimeRangeInputProps): React.ReactElement {
  const startTrim = trimToHhMm(start)
  const endTrim = trimToHhMm(end)
  const invalid = startTrim !== '' && endTrim !== '' && startTrim >= endTrim

  function handleStartChange(raw: string): void {
    onChange({ start: trimToHhMm(raw), end })
  }
  function handleEndChange(raw: string): void {
    onChange({ start, end: trimToHhMm(raw) })
  }
  function handleSwap(): void {
    onChange({ start: endTrim, end: startTrim })
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor={startId}>Das</Label>
          <Input
            id={startId}
            type="time"
            step={60}
            value={startTrim}
            onChange={(e) => handleStartChange(e.target.value)}
            disabled={disabled}
            className="w-32"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={endId}>até</Label>
          <Input
            id={endId}
            type="time"
            step={60}
            value={endTrim}
            onChange={(e) => handleEndChange(e.target.value)}
            disabled={disabled}
            className="w-32"
          />
        </div>
      </div>
      {invalid && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <div className="flex-1 min-w-[180px]">
            Horário de início ({startTrim}) precisa ser ANTES de fim ({endTrim}).
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleSwap}
            disabled={disabled}
          >
            Trocar automaticamente
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * Trim defensivo `HH:mm:ss` → `HH:mm`. Native `<input type="time">` retorna
 * `HH:mm` em browsers modernos, mas algumas variants Android entregam
 * `HH:mm:ss`. Backend valida `HH:mm` strict; este trim previne 422.
 */
function trimToHhMm(value: string): string {
  if (value.length <= 5) return value
  return value.slice(0, 5)
}
